import { Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./live-preview/cursor";
import {
  startCompletion,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import { EditorView } from "@codemirror/view";

// Matches {{ .variable_name }} — dot prefix distinguishes from control flow like {{ if }}
const variablePattern = /\{\{\s*\.([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;
const variablePillMark = Decoration.mark({ class: "cm-variable-pill" });

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

/**
 * Build decorations for {{ .variable_name }} workflow variable references.
 * Same pattern as buildContextRefDecorations:
 * - Cursor NOT on line → show styled pill with variable name
 * - Cursor ON line → show raw {{ .variable_name }}
 */
export function buildVariableDecorations(
  state: EditorState,
  visibleRange: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  const cursorLine = cursor.cursorLine;
  const text = state.doc.sliceString(visibleRange.from, visibleRange.to);

  let match: RegExpExecArray | null;
  variablePattern.lastIndex = 0;

  while ((match = variablePattern.exec(text)) !== null) {
    const start = visibleRange.from + match.index;
    const end = start + match[0].length;
    const nodeLine = state.doc.lineAt(start).number;

    if (cursorLine === nodeLine) continue;

    const varName = match[1];
    const fullMatch = match[0];
    const dotIndex = fullMatch.indexOf(".");
    const nameStart = start + dotIndex + 1;
    const nameEnd = nameStart + varName.length;

    // Hide {{ .
    decorations.push({
      from: start,
      to: nameStart,
      decoration: Decoration.replace({}),
    });

    // Style variable name as pill
    decorations.push({
      from: nameStart,
      to: nameEnd,
      decoration: variablePillMark,
    });

    // Hide  }}
    decorations.push({
      from: nameEnd,
      to: end,
      decoration: Decoration.replace({}),
    });
  }
}

export interface WorkflowVariableDef {
  name: string;
  label: string;
  type: string;
}

/**
 * Creates a completion source for workflow variables.
 * Triggered when user types "{{ ." — shows all defined variables.
 */
export function variableCompletions(variablesDef: WorkflowVariableDef[]) {
  return (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos);
    const textBefore = line.text.slice(0, context.pos - line.from);

    // Look for {{ . pattern before cursor
    const triggerMatch = textBefore.match(/\{\{\s*\.([a-zA-Z_][a-zA-Z0-9_]*)?$/);
    if (!triggerMatch) return null;

    const partialName = triggerMatch[1] ?? "";
    const from = context.pos - partialName.length;

    return {
      from,
      to: context.pos,
      options: variablesDef.map((v) => ({
        label: v.name,
        detail: `${v.label} (${v.type})`,
        apply: (view: EditorView, _completion: any, from: number, to: number) => {
          const insert = `${v.name} }}`;
          view.dispatch({
            changes: { from, to, insert },
            selection: { anchor: from + insert.length },
          });
        },
        type: "variable",
      })),
    };
  };
}

/**
 * Insert {{ . at cursor and trigger autocomplete.
 * Used by a toolbar button.
 */
export function insertVariable(view: EditorView): void {
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: "{{ ." },
    selection: { anchor: from + 4 },
  });
  setTimeout(() => startCompletion(view), 50);
}
